import React, { useEffect, useState } from 'react';

/**
 * Barra de accesibilidad GOV.CO (Kit UI v9.2 — Componentes transversales).
 *
 * Módulo de acciones que facilita la navegabilidad para usuarios con condiciones
 * especiales: permite aumentar/disminuir el tamaño de los textos y cambiar el
 * contraste de todo el portal. Requisitos aplicados:
 *  - Visible y de fácil acceso, ubicado al centro-derecha de la pantalla.
 *  - Presente de forma consistente en todas las páginas (se monta una sola vez).
 *  - Navegable y operable por teclado, con foco visible.
 *  - NO se muestra desde el breakpoint de tablet hacia abajo (los dispositivos
 *    móviles cuentan con opciones de accesibilidad nativas).
 *  - Las preferencias se conservan entre páginas y sesiones (localStorage).
 */
const FONT_KEY = 'a11y-font-scale';
const CONTRAST_KEY = 'a11y-contrast';
const MIN_SCALE = 0.875;
const MAX_SCALE = 1.5;
const STEP = 0.125;

const clamp = (v) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, v));

const AccessibilityBar = () => {
  const [scale, setScale] = useState(() => {
    if (typeof window === 'undefined') return 1;
    const stored = parseFloat(window.localStorage.getItem(FONT_KEY));
    return Number.isNaN(stored) ? 1 : clamp(stored);
  });
  const [contrast, setContrast] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(CONTRAST_KEY) === 'true';
  });

  // Aplica el tamaño de fuente al documento (escala las medidas en rem).
  useEffect(() => {
    document.documentElement.style.fontSize = `${(scale * 100).toFixed(1)}%`;
    window.localStorage.setItem(FONT_KEY, String(scale));
  }, [scale]);

  // Activa/desactiva el modo de alto contraste (clase en <html>, estilos en index.css).
  useEffect(() => {
    document.documentElement.classList.toggle('a11y-contrast', contrast);
    window.localStorage.setItem(CONTRAST_KEY, String(contrast));
  }, [contrast]);

  const increase = () => setScale((v) => clamp(+(v + STEP).toFixed(3)));
  const decrease = () => setScale((v) => clamp(+(v - STEP).toFixed(3)));

  const BTN =
    'group relative flex items-center justify-center w-11 h-11 bg-cobalt text-white border-t border-white/20 first:border-t-0 ' +
    'transition-colors hover:bg-cobalt-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white ' +
    'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-cobalt';

  // Etiqueta emergente (tooltip) que aparece a la izquierda en hover/focus.
  // Se oculta con `hidden` (display:none) — NO con opacity-0 — para que WAVE no la
  // evalúe como contraste; los elementos display:none quedan fuera del análisis.
  const TOOLTIP =
    'pointer-events-none absolute right-full mr-1 px-3 py-1 rounded bg-cobalt text-white text-[13px] font-semibold whitespace-nowrap ' +
    'hidden group-hover:block group-focus:block';

  return (
    <div
      role="toolbar"
      aria-orientation="vertical"
      aria-label="Herramientas de accesibilidad"
      className="hidden lg:flex fixed right-0 top-1/2 -translate-y-1/2 z-[1100] flex-col rounded-l-lg overflow-hidden shadow-[0_4px_16px_rgba(0,0,0,0.25)]"
    >
      {/* Contraste */}
      <button type="button" onClick={() => setContrast((c) => !c)} aria-pressed={contrast} aria-label="Cambiar contraste de la página" className={BTN}>
        <span aria-hidden="true" className={TOOLTIP}>{contrast ? 'Quitar contraste' : 'Contraste'}</span>
        <svg aria-hidden="true" viewBox="0 0 24 24" className="w-5 h-5">
          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" />
        </svg>
      </button>

      {/* Aumentar tamaño de letra */}
      <button type="button" onClick={increase} disabled={scale >= MAX_SCALE} aria-label="Aumentar tamaño de la letra" className={BTN}>
        <span aria-hidden="true" className={TOOLTIP}>Aumentar letra</span>
        <span aria-hidden="true" className="font-bold leading-none text-[17px]">
          A<span className="align-super text-[11px]">+</span>
        </span>
      </button>

      {/* Reducir tamaño de letra */}
      <button type="button" onClick={decrease} disabled={scale <= MIN_SCALE} aria-label="Reducir tamaño de la letra" className={BTN}>
        <span aria-hidden="true" className={TOOLTIP}>Reducir letra</span>
        <span aria-hidden="true" className="font-bold leading-none text-[13px]">
          A<span className="align-super text-[9px]">−</span>
        </span>
      </button>
    </div>
  );
};

export default AccessibilityBar;
